import { showUnknownPayment } from '../../Common/utils/paymentResult';
import useUnmeRetryPayment, { isHandledUnmeRetry, safeUnmePaymentMessage, UNME_FALLBACK_ENABLED } from '../../Common/hooks/useUnmeRetryPayment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LoaderCircle } from 'lucide-react';
import { checkLogin, setOtpVerified } from '../../Common/store';
import { isValidEmail, isValidPhone, normalizePhone } from '../../Common/hooks/useManualPaymentConfig';
import PaymentMemoPanel from '../components/PaymentMemoPanel';
import {
  getTerminalPaymentProfile,
  submitMappedTerminalPayment,
  updateTerminalPaymentProfile,
} from '../api/cp';
import { getDemoPaymentBlockMessage, isDemoPaymentBlocked, request } from '../../Common/api/client';

function normalizePharmacy(row) {
  if (!row) return null;
  const mappingId = row.mappingId ?? row.mapping_id;
  const selectionToken = String(row.selectionToken ?? row.selection_token ?? '');
  const hasMappingId = mappingId !== undefined && mappingId !== null && mappingId !== '';
  if (!hasMappingId && !selectionToken) return null;
  return {
    mappingId: hasMappingId ? Number(mappingId) : null,
    selectionToken,
    pharmacyId: row.pharmacyId ?? row.pharmacy_id ?? row.id,
    institutionCode: String(row.institutionCode ?? row.institution_code ?? ''),
    externalCustomerCode: String(row.externalCustomerCode ?? row.external_customer_code ?? row.drugCode ?? row.drug_code ?? row.code ?? ''),
    name: String(row.name ?? row.customer_name ?? row.drugName ?? row.drug_name ?? ''),
    phone: String(row.phone ?? row.pharmacy_phone ?? ''),
    address: row.address ?? '',
    businessNo: String(row.businessNo ?? row.business_no ?? ''),
    representativeName: String(row.representativeName ?? row.representative_name ?? ''),
    drugSaleCode: String(row.drugSaleCode ?? row.drug_sale_code ?? ''),
    drugSaleName: String(row.drugSaleName ?? row.drug_sale_name ?? ''),
    isDemo: row.isDemo === true || row.is_demo === true,
  };
}

function normalizePaymentProfile(profile) {
  return {
    mappingId: Number(profile?.mappingId ?? profile?.mapping_id ?? 0),
    name: String(profile?.name ?? ''),
    externalCustomerCode: String(profile?.externalCustomerCode ?? profile?.external_customer_code ?? ''),
    paymentBuyerEmail: String(profile?.paymentBuyerEmail ?? profile?.payment_buyer_email ?? '').trim(),
    paymentBuyerPhone: normalizePhone(profile?.paymentBuyerPhone ?? profile?.payment_buyer_phone ?? ''),
    version: Number(profile?.version ?? 0),
    updatedAt: profile?.updatedAt ?? profile?.updated_at ?? null,
  };
}

function byteLength(value) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(String(value || '')).length;
  return unescape(encodeURIComponent(String(value || ''))).length;
}

function isValidCardNumberLength(value) {
  return value.length === 15 || value.length === 16;
}

function createManualTradeNo() {
  return 'manual_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
}
function firstNonBlank(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return "";
}

function getPaymentTransactionId(payment) {
  return firstNonBlank(
    payment?.paymentTransactionId,
    payment?.payment_transaction_id,
    payment?.paymentTransaction?.id,
    payment?.payment_transaction?.id,
    payment?.transaction?.id,
    payment?.id,
  );
}

function getPaymentApprovalNo(payment) {
  const provider = payment?.providerResult || payment?.provider_result || {};
  const transaction = payment?.paymentTransaction || payment?.payment_transaction || payment?.transaction || {};
  return firstNonBlank(
    payment?.approvalNo, payment?.approval_no, payment?.approvalNumber, payment?.apprNo, payment?.appr_no,
    payment?.appNo, payment?.app_no, payment?.authNo, payment?.auth_no,
    transaction?.approvalNo, transaction?.approval_no, transaction?.approvalNumber, transaction?.apprNo, transaction?.appr_no,
    provider?.approvalNo, provider?.approval_no, provider?.approvalNumber, provider?.apprNo, provider?.appr_no,
    provider?.outApplNo, provider?.out_appl_no, provider?.appNo, provider?.app_no,
  );
}

function getPaymentSalespersonName(payment) {
  const terminal = payment?.terminal || {};
  const transaction = payment?.paymentTransaction || payment?.payment_transaction || payment?.transaction || {};
  return firstNonBlank(
    terminal?.name, terminal?.drugSaleName, terminal?.drug_sale_name,
    payment?.salespersonName, payment?.salesperson_name, payment?.terminalName, payment?.terminal_name,
    payment?.drugSaleName, payment?.drug_sale_name,
    transaction?.salespersonName, transaction?.salesperson_name, transaction?.terminalName, transaction?.terminal_name,
  );
}

function getPaymentRows(data) {
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
}


export default function MappedManualPayment({
  config,
  initialPharmacy,
  variant = 'A',
  externalCustomerMode = false,
  externalCustomer = null,
  externalCustomerField = null,
  topContent = null,
  onExternalSelectionInvalid,
  onExternalPaymentCompleted,
  submitPayment = submitMappedTerminalPayment,
  loginPath = '/app/login',
  submitButtonLabel = '결제하기',
  salespersonLabel = '영업자명',
  defaultProductName = '',
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { runPayment, acknowledgeSuccessfulPayment, retryUi, retryBlocked } = useUnmeRetryPayment(loginPath.startsWith('/cp/') ? 'cp' : 'terminal');
  const [selected, setSelected] = useState(() => normalizePharmacy(externalCustomerMode ? externalCustomer : initialPharmacy));
  const [amount, setAmount] = useState('');
  const [cardNo, setCardNo] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [installment, setInstallment] = useState('00');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [profileLoading, setProfileLoading] = useState(!externalCustomerMode && Boolean(normalizePharmacy(initialPharmacy)));
  const [profileSaving, setProfileSaving] = useState(false);
  const [baseProfile, setBaseProfile] = useState(null);
  const [pendingProfileSave, setPendingProfileSave] = useState(null);
  const [completedPayment, setCompletedPayment] = useState(null);
  const [failedPayment, setFailedPayment] = useState(null);
  const [paymentResolutionRequired, setPaymentResolutionRequired] = useState(false);
  const paymentAttemptIdRef = useRef('');
  const unmeSubmitLock = useRef(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'error' });
  const sessionSalespersonName = useMemo(() => {
    try {
      return window.localStorage.getItem('app_saved_terminal_name') || '';
    } catch (_) {
      return '';
    }
  }, []);
  const salespersonName = selected?.drugSaleName || sessionSalespersonName;

  const generatedProductName = externalCustomerMode
    ? defaultProductName
    : selected
      ? String(selected.externalCustomerCode || '') + '_' + String(selected.name || '')
      : '';
  const [productName, setProductName] = useState(() => {
    if (externalCustomerMode) return defaultProductName;
    const initial = normalizePharmacy(initialPharmacy);
    return initial
      ? String(initial.externalCustomerCode || '') + '_' + String(initial.name || '')
      : '';
  });
  const memoCustomerKey = selected?.externalCustomerCode
    || (selected?.mappingId ? 'mapping-' + selected.mappingId : '');

  const showToast = useCallback((message, type = 'error') => {
    setToast({ visible: true, message, type });
    window.setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2000);
  }, []);

  useEffect(() => {
    if (!externalCustomerMode) return;
    setSelected(normalizePharmacy(externalCustomer));
  }, [externalCustomer, externalCustomerMode]);

  useEffect(() => {
    if (!externalCustomerMode) return;
    paymentAttemptIdRef.current = '';
    setPaymentResolutionRequired(false);
  }, [externalCustomer?.selectionToken, externalCustomerMode]);

  useEffect(() => {
    if (!selected) {
      if (!externalCustomerMode) navigate('/app/dashboard', { replace: true });
      return;
    }
    setProductName(generatedProductName);
  }, [externalCustomerMode, generatedProductName, navigate, selected]);

  useEffect(() => {
    if (externalCustomerMode) {
      setProfileLoading(false);
      setBaseProfile(null);
      setPendingProfileSave(null);
      return undefined;
    }
    if (!selected?.mappingId) return undefined;

    let cancelled = false;
    setProfileLoading(true);
    setBaseProfile(null);
    setPendingProfileSave(null);
    setEmail('');
    setPhone('');

    getTerminalPaymentProfile(selected.mappingId)
      .then((response) => {
        if (cancelled) return;
        const profile = normalizePaymentProfile(response);
        setBaseProfile(profile);
        setEmail(profile.paymentBuyerEmail);
        setPhone(profile.paymentBuyerPhone);
      })
      .catch((error) => {
        if (cancelled) return;
        const code = Number(error?.code ?? error?.response?.data?.code);
        setBaseProfile(null);
        setEmail('');
        setPhone('');
        if (code === 4040) {
          setSelected(null);
          navigate('/app/dashboard', {
            replace: true,
            state: { message: '선택한 거래처 매핑이 변경되었습니다. 거래처를 다시 선택해 주세요.' },
          });
        } else if (code === 9999) {
          dispatch(setOtpVerified(false));
          dispatch(checkLogin(false));
          navigate(loginPath, { replace: true });
        } else {
          showToast('기본 연락처를 불러오지 못했습니다. 직접 입력해 주세요.');
        }
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, externalCustomerMode, loginPath, navigate, selected?.mappingId, showToast]);

  const productBytes = useMemo(() => byteLength(productName), [productName]);
  const productTooLong = productBytes > Number(config?.productNameMaxBytes || 255);
  const normalizedPhone = normalizePhone(phone);
  const amountNumber = Number(amount || 0);
  const showPaymentFailure = (message, title = '결제에 실패했습니다.') => {
    setFailedPayment({
      title,
      message: String(message || '결제 요청 중 오류가 발생했습니다.'),
      customerName: selected?.name || '',
      amount: amountNumber,
    });
  };
  const paymentBlocked = config?.paymentEnabled === false;
  const paymentBlockReason = config?.paymentBlockReason || '시연 계정에서는 실제 결제를 진행할 수 없습니다.';

  const valid = Boolean(
    !paymentBlocked &&
    (externalCustomerMode ? selected?.selectionToken : selected?.mappingId) &&
    !profileLoading &&
    !profileSaving &&
    !pendingProfileSave &&
    productName.trim() &&
    amountNumber > 0 &&
    isValidCardNumberLength(cardNo) &&
    Number(expMonth) >= 1 && Number(expMonth) <= 12 &&
    expYear.length === 2 &&
    (!config?.buyerEmailRequired || isValidEmail(email)) &&
    (!config?.buyerPhoneRequired || isValidPhone(normalizedPhone)) &&
    !productTooLong &&
    !paymentResolutionRequired
  );
  const paymentFieldsDisabled = externalCustomerMode && !selected;

  const resetPaymentFields = () => {
    setAmount('');
    setCardNo('');
    setExpMonth('');
    setExpYear('');
    setInstallment('00');
    setProductName(generatedProductName);
  };

  const submit = async () => {
    if (!valid || submitting || retryBlocked || (UNME_FALLBACK_ENABLED && unmeSubmitLock.current)) return;
    if (UNME_FALLBACK_ENABLED) unmeSubmitLock.current = true;
    const finalEmail = email.trim();
    const finalPhone = normalizedPhone;
    if (!paymentAttemptIdRef.current) paymentAttemptIdRef.current = createManualTradeNo();
    const mchtTrdNo = paymentAttemptIdRef.current;
    setSubmitting(true);
    try {
      const result = await runPayment({
        ...(externalCustomerMode
          ? { gasystemCustomerToken: selected.selectionToken }
          : { customerMapId: selected.mappingId }),
        mchtTrdNo,
        trdAmt: amountNumber,
        instmtMon: installment,
        pmtprdNm: productName.trim(),
        mchtCustNm: selected.name,
        mchtCustId: selected.externalCustomerCode,
        email: finalEmail,
        phone: finalPhone,
        cardNo,
        vldDtMon: expMonth.padStart(2, '0'),
        vldDtYear: expYear.slice(-2),
      }, submitPayment);
      if (showUnknownPayment(result, loginPath.startsWith('/cp/') ? 'cp' : 'terminal')) { resetPaymentFields(); paymentAttemptIdRef.current = ''; return; }
      if (result?.replayed === true && !UNME_FALLBACK_ENABLED) {
        resetPaymentFields();
        paymentAttemptIdRef.current = '';
        setPaymentResolutionRequired(false);
        if (externalCustomerMode) onExternalPaymentCompleted?.();
        showToast('이미 처리된 결제 요청입니다. 결제내역을 확인해 주세요.', 'success');
        return;
      }
      const status = String(result?.status || '').toUpperCase();
      if (status !== 'COMPLETED') {
        if (UNME_FALLBACK_ENABLED && status === 'FAILED') {
          resetPaymentFields();
          paymentAttemptIdRef.current = '';
          throw new Error(safeUnmePaymentMessage(result?.msg));
        }
        throw new Error(result?.outRsltMsg || result?.provider_result?.outRsltMsg || '결제에 실패했습니다.');
      }

      const paymentTransactionId = Number(getPaymentTransactionId(result) || 0);
      const completed = {
        customerName: firstNonBlank(result?.customer?.drugName, result?.customer?.drug_name, selected?.name),
        salespersonName: firstNonBlank(getPaymentSalespersonName(result), selected?.drugSaleName, salespersonName),
        amount: amountNumber,
        approvalNo: getPaymentApprovalNo(result),
      };
      setCompletedPayment(completed);

      if (paymentTransactionId && (!completed.salespersonName || !completed.approvalNo)) {
        const paymentListUrl = loginPath.startsWith("/cp/")
          ? "/api/cp/payment/manual"
          : "/api/terminal/payment/manual";
        request({ method: "GET", url: paymentListUrl, params: { page: 1, limit: 20 } })
          .then((data) => {
            const paymentRow = getPaymentRows(data).find(
              (row) => String(getPaymentTransactionId(row)) === String(paymentTransactionId),
            );
            if (!paymentRow) return;
            setCompletedPayment((current) => current ? {
              ...current,
              salespersonName: current.salespersonName || getPaymentSalespersonName(paymentRow),
              approvalNo: current.approvalNo || getPaymentApprovalNo(paymentRow),
            } : current);
          })
          .catch(() => {});
      }
      resetPaymentFields();
      paymentAttemptIdRef.current = '';
      setPaymentResolutionRequired(false);
      if (externalCustomerMode) {
        onExternalPaymentCompleted?.();
        return;
      }
      const initialEmail = baseProfile?.paymentBuyerEmail || '';
      const initialPhone = normalizePhone(baseProfile?.paymentBuyerPhone || '');
      const profileChanged = finalEmail !== initialEmail || finalPhone !== initialPhone;

      if (profileChanged && paymentTransactionId) {
        setPendingProfileSave({
          paymentBuyerEmail: finalEmail,
          paymentBuyerPhone: finalPhone,
          sourcePaymentTransactionId: paymentTransactionId,
          expectedVersion: Number(baseProfile?.version || 0),
          originalEmail: initialEmail,
          originalPhone: initialPhone,
          conflicted: false,
        });
      } else {
        setEmail(initialEmail);
        setPhone(initialPhone);
        if (profileChanged) {
          showToast('결제는 완료됐지만 기본 연락처 저장 정보를 확인하지 못했습니다.');
        }
      }
    } catch (error) {
      if (isHandledUnmeRetry(error)) {
        resetPaymentFields();
        paymentAttemptIdRef.current = '';
        if (error.blocked) setPaymentResolutionRequired(true);
        return;
      }
      if (showUnknownPayment(error?.data ?? error?.response?.data, loginPath.startsWith('/cp/') ? 'cp' : 'terminal')) { resetPaymentFields(); paymentAttemptIdRef.current = ''; return; }
      if (isDemoPaymentBlocked(error)) {
        showPaymentFailure(getDemoPaymentBlockMessage(error), '시연 모드 안내');
        return;
      }
      const code = Number(error?.code ?? error?.response?.data?.code);
      if (externalCustomerMode && (code === 4000 || code === 4030)) {
        paymentAttemptIdRef.current = '';
        setPaymentResolutionRequired(false);
        onExternalSelectionInvalid?.();
        showPaymentFailure('거래처 선택 시간이 만료되었습니다. 거래처를 다시 검색해 주세요.');
      } else if (externalCustomerMode && code === 4040) {
        paymentAttemptIdRef.current = '';
        setPaymentResolutionRequired(false);
        onExternalSelectionInvalid?.();
        showPaymentFailure('담당자 또는 가맹점이 비활성 상태입니다. 관리자에게 문의해 주세요.');
      } else if (externalCustomerMode && code === 4090) {
        setPaymentResolutionRequired(true);
        showPaymentFailure('결제 요청이 처리 중이거나 이미 사용된 요청입니다. 결제내역에서 결과를 먼저 확인해 주세요.', '결제 결과 확인이 필요합니다.');
      } else if (code === 4040) {
        setSelected(null);
        navigate('/app/dashboard', {
          replace: true,
          state: { message: '선택한 거래처 매핑이 변경되었습니다. 거래처를 다시 선택해 주세요.' },
        });
      } else if (code === 9999) {
        dispatch(setOtpVerified(false));
        dispatch(checkLogin(false));
        navigate(loginPath, { replace: true });
      } else {
        showPaymentFailure(UNME_FALLBACK_ENABLED
          ? safeUnmePaymentMessage(error?.response?.data?.msg || error?.message, '결제 요청을 확인하지 못했습니다.')
          : error?.message || error?.response?.data?.msg || '결제 요청 중 오류가 발생했습니다.');
      }
    } finally {
      unmeSubmitLock.current = false;
      setSubmitting(false);
    }
  };

  const useForThisPaymentOnly = () => {
    setEmail(baseProfile?.paymentBuyerEmail || '');
    setPhone(baseProfile?.paymentBuyerPhone || '');
    setPendingProfileSave(null);
    showToast('결제가 완료되었습니다.', 'success');
  };

  const savePaymentProfile = async () => {
    if (!pendingProfileSave || profileSaving) return;
    setProfileSaving(true);
    try {
      const saved = normalizePaymentProfile(await updateTerminalPaymentProfile(selected.mappingId, {
        paymentBuyerEmail: pendingProfileSave.paymentBuyerEmail,
        paymentBuyerPhone: pendingProfileSave.paymentBuyerPhone,
        sourcePaymentTransactionId: pendingProfileSave.sourcePaymentTransactionId,
        expectedVersion: pendingProfileSave.expectedVersion,
      }));
      setBaseProfile(saved);
      setEmail(saved.paymentBuyerEmail);
      setPhone(saved.paymentBuyerPhone);
      setPendingProfileSave(null);
      showToast('결제 및 기본 연락처 저장이 완료되었습니다.', 'success');
    } catch (error) {
      const code = Number(error?.code ?? error?.response?.data?.code);
      if (code === 4090) {
        const latestRaw = error?.data?.profile ?? error?.response?.data?.data?.profile;
        if (latestRaw) {
          const latest = normalizePaymentProfile(latestRaw);
          setBaseProfile(latest);
          setEmail(latest.paymentBuyerEmail);
          setPhone(latest.paymentBuyerPhone);
          setPendingProfileSave((current) => current ? {
            ...current,
            expectedVersion: latest.version,
            originalEmail: latest.paymentBuyerEmail,
            originalPhone: latest.paymentBuyerPhone,
            conflicted: true,
          } : current);
          showToast('다른 사용자가 먼저 변경했습니다. 최신 값을 확인해 주세요.');
        } else {
          showToast('최신 기본 연락처를 확인하지 못했습니다.');
        }
      } else if (code === 4040) {
        setEmail(baseProfile?.paymentBuyerEmail || '');
        setPhone(baseProfile?.paymentBuyerPhone || '');
        setPendingProfileSave(null);
        showToast('결제는 완료됐지만 거래처 상태가 변경되어 기본 연락처를 저장하지 못했습니다.');
      } else if (code === 9999) {
        dispatch(setOtpVerified(false));
        dispatch(checkLogin(false));
        navigate(loginPath, { replace: true });
      } else {
        showToast('결제는 완료됐지만 기본 연락처를 저장하지 못했습니다.');
      }
    } finally {
      setProfileSaving(false);
    }
  };

  const profileSaveModal = pendingProfileSave ? (
    <PaymentProfileSaveModal
      pending={pendingProfileSave}
      saving={profileSaving}
      onUseOnce={useForThisPaymentOnly}
      onSave={savePaymentProfile}
    />
  ) : null;

  if (variant === 'B') {
    return (
      <div style={bStyles.page}>
        {retryUi}
        {externalCustomerMode && submitting && <PaymentProcessingOverlay />}
        {toast.visible && (
          <div role="status" style={{ ...styles.toast, background: toast.type === 'success' ? '#0b7a3e' : '#e5484d' }}>
            {toast.message}
          </div>
        )}
        <div style={bStyles.scroll}>
          {topContent}
          <div style={bStyles.form}>
            {profileLoading && <div role="status" style={styles.profileLoading}>거래처 기본 연락처를 불러오는 중입니다.</div>}
            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 2 }}>
                <label style={bStyles.label}>거래처명</label>
                {externalCustomerMode && externalCustomerField ? externalCustomerField : (
                  <input
                    type="text"
                    readOnly
                    value={selected?.name || ''}
                    aria-label="거래처명"
                    title="담당 거래처 목록에서 선택되어 수정할 수 없습니다."
                    style={{ ...bStyles.input, ...bStyles.fixedInput }}
                  />
                )}
              </div>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <label style={bStyles.label}>{salespersonLabel}</label>
                <input
                  type="text"
                  readOnly
                  value={salespersonName}
                  aria-label="영업자명"
                  title="자동 매핑되어 수정할 수 없습니다."
                  style={{ ...bStyles.input, ...bStyles.fixedInput }}
                />
              </div>
            </div>

            {paymentFieldsDisabled && (
              <div role="status" style={bStyles.selectionRequiredNotice}>
                결제정보를 입력하려면 거래처를 검색해 선택해 주세요.
              </div>
            )}
            {paymentResolutionRequired && (
              <div role="alert" style={bStyles.resolutionRequiredNotice}>
                기존 결제 요청의 처리 결과를 결제내역에서 확인한 후 다시 진행해 주세요.
              </div>
            )}

            <fieldset
              disabled={paymentFieldsDisabled || paymentResolutionRequired || submitting}
              aria-disabled={paymentFieldsDisabled || paymentResolutionRequired || submitting}
              style={{ ...bStyles.paymentFields, opacity: paymentFieldsDisabled || paymentResolutionRequired ? 0.5 : 1 }}
            >

            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <RequiredLabel style={bStyles.label}>상품명</RequiredLabel>
                <input
                  type="text"
                  value={productName}
                  onChange={(event) => setProductName(event.target.value)}
                  aria-label="상품명"
                  autoComplete="off"
                  style={bStyles.input}
                />
                {productTooLong && <small style={bStyles.error}>상품명이 허용 길이를 초과했습니다.</small>}
              </div>
            </div>

            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 2 }}>
                <RequiredLabel style={bStyles.label}>카드번호</RequiredLabel>
                <input
                  value={cardNo.replace(/(\d{4})(?=\d)/g, '$1 ')}
                  onChange={(event) => {
                    const value = event.target.value.replace(/\D/g, '').slice(0, 16);
                    setCardNo(value);
                    if (value.length === 16) {
                      window.setTimeout(() => document.getElementById('secure-entry-b')?.focus(), 0);
                    }
                  }}
                  inputMode="numeric"
                  autoComplete="one-time-code" aria-autocomplete="none" data-lpignore="true" data-1p-ignore="true" data-form-type="other" data-autofill="off" spellCheck={false} autoCapitalize="off"
                  style={bStyles.input}
                />
              </div>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <RequiredLabel style={bStyles.label}>유효기간</RequiredLabel>
                <div style={bStyles.expiration}>
                  <input
                    id="secure-entry-b"
                    value={expMonth}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, '').slice(0, 2);
                      setExpMonth(value);
                      if (value.length === 2) window.setTimeout(() => document.getElementById('secure-entry-c')?.focus(), 0);
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code" aria-autocomplete="none" data-lpignore="true" data-1p-ignore="true" data-form-type="other" data-autofill="off" spellCheck={false} autoCapitalize="off"
                    placeholder="월"
                    style={bStyles.expirationInput}
                  />
                  <span style={bStyles.expirationSlash}>/</span>
                  <input
                    id="secure-entry-c"
                    value={expYear}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, '').slice(0, 2);
                      setExpYear(value);
                      if (value.length === 2) window.setTimeout(() => document.getElementById('mapped-amount')?.focus(), 0);
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code" aria-autocomplete="none" data-lpignore="true" data-1p-ignore="true" data-form-type="other" data-autofill="off" spellCheck={false} autoCapitalize="off"
                    placeholder="년"
                    style={bStyles.expirationInput}
                  />
                </div>
              </div>
            </div>

            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 2 }}>
                <RequiredLabel style={bStyles.label}>결제금액</RequiredLabel>
                <input
                  id="mapped-amount"
                  value={amount ? Number(amount).toLocaleString('ko-KR') : ''}
                  onChange={(event) => setAmount(event.target.value.replace(/\D/g, '').slice(0, 9))}
                  inputMode="numeric"
                  style={bStyles.input}
                />
              </div>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <label style={bStyles.label}>할부</label>
                <div style={bStyles.selectWrap}>
                  <select value={installment} onChange={(event) => setInstallment(event.target.value)} style={bStyles.select}>
                    <option value="00">일시불</option>
                    {Array.from({ length: 10 }, (_, index) => String(index + 1).padStart(2, '0')).map((value) => (
                      <option key={value} value={value}>{value}개월</option>
                    ))}
                  </select>
                  <ChevronDown size={20} color="#6b7280" style={bStyles.selectIcon} />
                </div>
              </div>
            </div>

            {config?.buyerEmailRequired && (
              <div style={bStyles.row}>
                <div style={{ ...bStyles.field, flex: 1 }}>
                  <RequiredLabel style={bStyles.label}>구매자 이메일</RequiredLabel>
                  <input
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    type="email"
                    autoComplete="email"
                    style={bStyles.input}
                  />
                </div>
              </div>
            )}

            {config?.buyerPhoneRequired && (
              <div style={bStyles.row}>
                <div style={{ ...bStyles.field, flex: 1 }}>
                  <RequiredLabel style={bStyles.label}>구매자 연락처</RequiredLabel>
                  <input
                    value={phone}
                    onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 15))}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    style={bStyles.input}
                  />
                </div>
              </div>
            )}
            <PaymentMemoPanel
              customerKey={memoCustomerKey}
              customerName={selected?.name}
              appearance="flat"
            />
            </fieldset>
          </div>
        </div>

        <div style={bStyles.bottom}>
          {paymentBlocked && <div role="status" style={styles.paymentBlockReason}>{paymentBlockReason}</div>}
          <button
            type="button"
            onClick={submit}
            disabled={!valid || submitting || paymentResolutionRequired}
            aria-busy={submitting}
            style={{ ...bStyles.submit, backgroundColor: valid && !submitting ? 'var(--brand-primary)' : '#cbd5e1' }}
          >
            {externalCustomerMode && submitting ? (
              <span style={bStyles.submittingLabel}><LoaderCircle size={19} className="animate-spin" />결제 처리 중...</span>
            ) : paymentResolutionRequired ? '결제 결과 확인 필요' : profileLoading ? '연락처 확인 중...' : submitting ? '요청중...' : submitButtonLabel}
          </button>
        </div>
        {profileSaveModal}
        {completedPayment && (
          <PaymentCompletedModal payment={completedPayment} onClose={() => { if (acknowledgeSuccessfulPayment()) setCompletedPayment(null); }} />
        )}
        {failedPayment && (
          <PaymentFailedModal payment={failedPayment} onClose={() => setFailedPayment(null)} />
        )}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {retryUi}
      {externalCustomerMode && submitting && <PaymentProcessingOverlay />}
      {toast.visible && (
        <div role="status" style={{ ...styles.toast, background: toast.type === 'success' ? '#0b7a3e' : '#e5484d' }}>
          {toast.message}
        </div>
      )}
      <div style={styles.scroll}>
        {topContent}
        <section style={styles.card}>
          <div style={styles.sectionHeader}>
            <strong>담당 거래처</strong>
          </div>
          <div style={styles.readonlyGrid}>
            <Info label="거래처코드" value={selected?.externalCustomerCode || selected?.institutionCode || '-'} />
            <Info label="약국명" value={selected?.name || '-'} />
            <Info label="사업자번호" value={selected?.businessNo || '-'} />
            <Info label={salespersonLabel} value={salespersonName || '-'} />
          </div>
          <div style={{ marginTop: 14 }}>
            <RequiredLabel style={styles.label}>상품명</RequiredLabel>
            <input
              type="text"
              value={productName}
              onChange={(event) => setProductName(event.target.value)}
              aria-label="상품명"
              autoComplete="off"
              style={styles.input}
            />
            {selected && (
              <small style={{ color: productTooLong ? '#e5484d' : '#8b95a1' }}>
                UTF-8 {productBytes}/{config?.productNameMaxBytes || 255} bytes
              </small>
            )}
          </div>
        </section>

        <section style={styles.card}>
          <strong style={{ display: 'block', marginBottom: 14 }}>결제정보</strong>
          {profileLoading && <div role="status" style={styles.profileLoading}>거래처 기본 연락처를 불러오는 중입니다.</div>}
          <Field label="결제금액" required>
            <input value={amount ? Number(amount).toLocaleString('ko-KR') : ''} onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 9))} inputMode="numeric" style={styles.input} />
          </Field>
          <Field label="카드번호" required>
            <input value={cardNo.replace(/(\d{4})(?=\d)/g, '$1 ')} onChange={(e) => setCardNo(e.target.value.replace(/\D/g, '').slice(0, 16))} inputMode="numeric" autoComplete="one-time-code" aria-autocomplete="none" data-lpignore="true" data-1p-ignore="true" data-form-type="other" data-autofill="off" spellCheck={false} autoCapitalize="off" style={styles.input} />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="유효기간 월" grow required>
              <input value={expMonth} onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" placeholder="MM" autoComplete="one-time-code" aria-autocomplete="none" data-lpignore="true" data-1p-ignore="true" data-form-type="other" data-autofill="off" spellCheck={false} autoCapitalize="off" style={styles.input} />
            </Field>
            <Field label="유효기간 년" grow required>
              <input value={expYear} onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" placeholder="YY" autoComplete="one-time-code" aria-autocomplete="none" data-lpignore="true" data-1p-ignore="true" data-form-type="other" data-autofill="off" spellCheck={false} autoCapitalize="off" style={styles.input} />
            </Field>
            <Field label="할부" grow>
              <select value={installment} onChange={(e) => setInstallment(e.target.value)} style={styles.input}>
                <option value="00">일시불</option>
                {Array.from({ length: 10 }, (_, i) => String(i + 1).padStart(2, '0')).map((v) => <option key={v} value={v}>{v}개월</option>)}
              </select>
            </Field>
          </div>
          {config?.buyerEmailRequired && (
            <Field label="구매자 이메일" required>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" style={styles.input} />
            </Field>
          )}
          {config?.buyerPhoneRequired && (
            <Field label="구매자 연락처" required>
              <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))} type="tel" inputMode="tel" autoComplete="tel" style={styles.input} />
            </Field>
          )}
        </section>
        <PaymentMemoPanel customerKey={memoCustomerKey} customerName={selected?.name} />
      </div>
      <div style={styles.bottom}>
        {paymentBlocked && <div role="status" style={styles.paymentBlockReason}>{paymentBlockReason}</div>}
        <button type="button" onClick={submit} disabled={!valid || submitting} aria-busy={submitting} style={{ ...styles.submit, opacity: valid && !submitting ? 1 : 0.45 }}>
          {externalCustomerMode && submitting ? (
            <span style={bStyles.submittingLabel}><LoaderCircle size={19} className="animate-spin" />결제 처리 중...</span>
          ) : profileLoading ? '연락처 확인 중...' : submitting ? '결제 요청 중...' : submitButtonLabel}
        </button>
      </div>
      {profileSaveModal}
      {completedPayment && (
        <PaymentCompletedModal payment={completedPayment} onClose={() => { if (acknowledgeSuccessfulPayment()) setCompletedPayment(null); }} />
      )}
      {failedPayment && (
        <PaymentFailedModal payment={failedPayment} onClose={() => setFailedPayment(null)} />
      )}
    </div>
  );
}

function PaymentProcessingOverlay() {
  return (
    <div role="status" aria-live="assertive" aria-label="결제 처리 중" style={styles.paymentProcessingOverlay}>
      <div style={styles.paymentProcessingCard}>
        <LoaderCircle size={42} className="animate-spin" color="var(--brand-primary)" aria-hidden="true" />
        <strong style={styles.paymentProcessingTitle}>결제를 처리하고 있습니다.</strong>
        <p style={styles.paymentProcessingDescription}>
          결과가 확인될 때까지 잠시만 기다려 주세요.<br />화면을 닫거나 뒤로 이동하지 마세요.
        </p>
      </div>
    </div>
  );
}

function PaymentCompletedModal({ payment, onClose }) {
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="payment-completed-title" style={styles.profileOverlay}>
      <div style={styles.paymentCompletedModal}>
        <div aria-hidden="true" style={styles.paymentCompletedIcon}>✓</div>
        <strong id="payment-completed-title" style={styles.profileModalTitle}>결제가 완료되었습니다.</strong>
        <div style={styles.paymentCompletedSummary}>
          <Info label="거래처명" value={payment.customerName || '-'} />
          <Info label="담당 영업자" value={payment.salespersonName || '-'} />
          <Info label="결제금액" value={`${Number(payment.amount || 0).toLocaleString('ko-KR')}원`} />
          <Info label="승인번호" value={payment.approvalNo || '-'} />
        </div>
        <button type="button" onClick={onClose} style={styles.paymentCompletedButton}>확인</button>
      </div>
    </div>
  );
}

function PaymentFailedModal({ payment, onClose }) {
  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="payment-failed-title" style={styles.profileOverlay}>
      <div style={styles.paymentCompletedModal}>
        <div aria-hidden="true" style={styles.paymentFailedIcon}>!</div>
        <strong id="payment-failed-title" style={styles.profileModalTitle}>{payment.title || '결제에 실패했습니다.'}</strong>
        <p style={styles.paymentFailedMessage}>{payment.message}</p>
        <div style={styles.paymentCompletedSummary}>
          <Info label="거래처명" value={payment.customerName || '-'} />
          <Info label="요청금액" value={Number(payment.amount || 0).toLocaleString('ko-KR') + '원'} />
        </div>
        <button type="button" onClick={onClose} style={styles.paymentFailedButton}>확인</button>
      </div>
    </div>
  );
}

function PaymentProfileSaveModal({ pending, saving, onUseOnce, onSave }) {
  const title = pending.conflicted ? '기본 연락처 변경 충돌' : '기본 연락처로 저장할까요?';
  return (
    <div style={styles.profileOverlay}>
      <div role="dialog" aria-modal="true" aria-label={title} style={styles.profileModal}>
        <strong style={styles.profileModalTitle}>{title}</strong>
        <p style={styles.profileModalDescription}>
          {pending.conflicted
            ? '다른 사용자가 기본 연락처를 먼저 변경했습니다. 최신 기본값을 확인한 뒤 다시 선택해 주세요.'
            : '이번 결제에 사용한 연락처가 기존 기본값과 다릅니다.'}
        </p>
        <div style={styles.profileCompare}>
          <ContactSummary
            label={pending.conflicted ? '최신 기본값' : '기존 기본값'}
            email={pending.originalEmail}
            phone={pending.originalPhone}
          />
          <div aria-hidden="true" style={styles.profileArrow}>↓</div>
          <ContactSummary
            label="이번 결제 입력값"
            email={pending.paymentBuyerEmail}
            phone={pending.paymentBuyerPhone}
            accent
          />
        </div>
        <p style={styles.profileModalNotice}>결제는 이미 완료되었습니다. 아래 선택은 이후 결제의 기본값에만 영향을 줍니다.</p>
        <div style={styles.profileModalActions}>
          <button type="button" onClick={onUseOnce} disabled={saving} style={styles.profileSecondaryButton}>
            이번 결제만 사용
          </button>
          <button type="button" onClick={onSave} disabled={saving} style={styles.profilePrimaryButton}>
            {saving ? '저장 중...' : '기본값으로 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactSummary({ label, email, phone, accent = false }) {
  return (
    <div style={{ ...styles.contactSummary, ...(accent ? styles.contactSummaryAccent : {}) }}>
      <strong style={styles.contactSummaryLabel}>{label}</strong>
      <span style={styles.contactSummaryValue}>이메일: {email || '없음'}</span>
      <span style={styles.contactSummaryValue}>연락처: {phone || '없음'}</span>
    </div>
  );
}

function Info({ label, value }) {
  return <div><span style={styles.infoLabel}>{label}</span><strong style={styles.infoValue}>{value}</strong></div>;
}

function RequiredLabel({ children, style }) {
  return (
    <label style={style}>
      {children} <span aria-hidden="true" style={{ color: '#e5484d' }}>*</span>
    </label>
  );
}

function Field({ label, children, grow = false, required = false }) {
  return (
    <label style={{ ...styles.field, ...(grow ? { flex: 1 } : {}) }}>
      <span style={styles.label}>
        {label}{required && <span aria-hidden="true" style={{ color: '#e5484d' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const bStyles = {
  page: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    color: '#191f28',
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 96,
    background: '#fff',
  },
  form: { padding: '20px 26px 24px', boxSizing: 'border-box' },
  paymentFields: { minWidth: 0, margin: 0, padding: 0, border: 0, transition: 'opacity 160ms ease' },
  selectionRequiredNotice: { margin: '-2px 0 13px', padding: '9px 11px', borderRadius: 9, background: '#f2f4f6', color: '#6b7684', fontSize: 11, fontWeight: 650, lineHeight: 1.45 },
  resolutionRequiredNotice: { margin: '-2px 0 13px', padding: '9px 11px', border: '1px solid #fdba74', borderRadius: 9, background: '#fff7ed', color: '#c2410c', fontSize: 11, fontWeight: 700, lineHeight: 1.45 },
  row: { display: 'flex', alignItems: 'flex-start', marginBottom: 13, gap: 12 },
  field: { minWidth: 0 },
  label: {
    display: 'block',
    padding: '0 4px',
    marginBottom: 2,
    color: '#666',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.45,
  },
  input: {
    width: '100%',
    height: 35,
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '2px 4px',
    border: 0,
    borderBottom: '1px solid #f0f0f0',
    borderRadius: 0,
    background: 'transparent',
    color: '#191f28',
    outline: 'none',
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '30px',
    fontVariantNumeric: 'tabular-nums',
  },
  fixedInput: {
    color: '#9aa4b2',
    cursor: 'default',
  },
  expiration: {
    display: 'flex',
    height: 35,
    alignItems: 'center',
    gap: 4,
    borderBottom: '1px solid #f0f0f0',
  },
  expirationInput: {
    width: 35,
    minWidth: 0,
    padding: '2px 4px',
    border: 0,
    background: 'transparent',
    color: '#191f28',
    outline: 'none',
    fontSize: 20,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  expirationSlash: { flexShrink: 0, color: '#6b7280', fontSize: 18, fontWeight: 500 },
  selectWrap: { position: 'relative', height: 35 },
  select: {
    width: '100%',
    height: 35,
    padding: '0 28px 0 4px',
    border: 0,
    borderBottom: '1px solid #f0f0f0',
    borderRadius: 0,
    background: 'transparent',
    color: '#191f28',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    fontSize: 20,
    fontWeight: 600,
  },
  selectIcon: {
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  error: { display: 'block', marginTop: 4, padding: '0 4px', color: '#e5484d', fontSize: 11 },
  bottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
    padding: '12px 16px calc(10px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    background: '#fff',
  },
  submit: {
    width: '100%',
    padding: 16,
    border: 0,
    borderRadius: 16,
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
  submittingLabel: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
};

const styles = {
  page: { position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f6f8f7' },
  scroll: { flex: 1, overflowY: 'auto', padding: '16px 16px 110px' },
  card: { background: '#fff', borderRadius: 16, padding: 16, marginBottom: 14, boxShadow: '0 3px 14px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  linkButton: { border: 0, background: '#eff8f2', color: 'var(--brand-primary)', borderRadius: 8, padding: '7px 12px', fontWeight: 700, cursor: 'pointer' },
  selectPrompt: { width: '100%', padding: 16, border: '1px dashed var(--brand-selection-border, #b8dec7)', borderRadius: 12, background: 'var(--brand-selection-background, #f8fcf9)', color: 'var(--brand-selection-text, #076931)', fontWeight: 700 },
  readonlyGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 10, padding: 12, background: 'var(--brand-surface-muted, #f8faf9)', borderRadius: 12 },
  readonlyValue: { minHeight: 38, padding: '10px 0', borderBottom: '1px solid #e5e8eb', color: '#191f28', fontWeight: 650, wordBreak: 'break-all' },
  fixedReadonlyValue: { padding: '10px 12px', borderBottomColor: '#d9dee5', borderRadius: 8, background: '#f2f4f6', color: '#6b7684' },
  infoLabel: { display: 'block', fontSize: 12, color: '#8b95a1', marginBottom: 2 },
  infoValue: { display: 'block', fontSize: 15, color: '#191f28' },
  field: { display: 'flex', flexDirection: 'column', marginBottom: 13, minWidth: 0 },
  label: { fontSize: 13, fontWeight: 650, color: '#4e5968', marginBottom: 6 },
  input: { width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #e5e8eb', borderRadius: 10, padding: '12px', background: '#fff', fontSize: 16, outline: 'none' },
  bottom: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: 'rgba(255,255,255,0.96)', borderTop: '1px solid #edf0ee' },
  paymentBlockReason: { marginBottom: 8, color: '#6d28d9', fontSize: 12, fontWeight: 700, lineHeight: 1.4, textAlign: 'center' },
  submit: { width: '100%', border: 0, borderRadius: 14, padding: 15, background: 'var(--brand-primary)', color: '#fff', fontSize: 16, fontWeight: 750 },
  profileLoading: { marginBottom: 12, padding: '9px 11px', borderRadius: 8, background: '#f2f7f4', color: '#4e5968', fontSize: 12 },
  profileOverlay: { position: 'fixed', inset: 0, zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box', background: 'rgba(15, 23, 42, 0.48)' },
  profileModal: { width: '100%', maxWidth: 420, boxSizing: 'border-box', padding: 20, borderRadius: 18, background: '#fff', boxShadow: '0 20px 50px rgba(15, 23, 42, 0.24)' },
  paymentCompletedModal: { width: '100%', maxWidth: 380, boxSizing: 'border-box', padding: '24px 20px 20px', borderRadius: 20, background: '#fff', textAlign: 'center', boxShadow: '0 20px 50px rgba(15, 23, 42, 0.24)' },
  paymentCompletedIcon: { width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', borderRadius: '50%', background: '#e8f7ee', color: 'var(--brand-primary)', fontSize: 25, fontWeight: 900 },
  paymentFailedIcon: { width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', borderRadius: '50%', background: '#feecec', color: '#e5484d', fontSize: 25, fontWeight: 900 },
  paymentFailedMessage: { margin: '10px 0 0', color: '#4e5968', fontSize: 14, lineHeight: 1.55, wordBreak: 'keep-all' },
  paymentFailedButton: { width: '100%', marginTop: 18, padding: '12px 16px', border: 0, borderRadius: 12, background: '#e5484d', color: '#fff', fontSize: 15, fontWeight: 750, cursor: 'pointer' },
  paymentCompletedSummary: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 18, padding: 14, borderRadius: 12, background: 'var(--brand-surface-muted, #f8faf9)', textAlign: 'left' },
  paymentCompletedButton: { width: '100%', marginTop: 18, padding: '12px 16px', border: 0, borderRadius: 12, background: 'var(--brand-primary)', color: '#fff', fontSize: 15, fontWeight: 750, cursor: 'pointer' },
  profileModalTitle: { display: 'block', color: '#191f28', fontSize: 19, lineHeight: 1.35 },
  profileModalDescription: { margin: '8px 0 14px', color: '#4e5968', fontSize: 14, lineHeight: 1.5 },
  profileCompare: { display: 'flex', flexDirection: 'column', gap: 7 },
  contactSummary: { display: 'flex', flexDirection: 'column', gap: 4, padding: 11, border: '1px solid #e5e8eb', borderRadius: 10, background: 'var(--brand-surface-muted, #f8faf9)' },
  contactSummaryAccent: { borderColor: '#8bcba6', background: '#eff8f2' },
  contactSummaryLabel: { marginBottom: 2, color: '#4e5968', fontSize: 12 },
  contactSummaryValue: { color: '#191f28', fontSize: 13, lineHeight: 1.4, overflowWrap: 'anywhere' },
  profileArrow: { alignSelf: 'center', color: '#8b95a1', lineHeight: 1 },
  profileModalNotice: { margin: '12px 0 16px', color: '#8b95a1', fontSize: 12, lineHeight: 1.45 },
  profileModalActions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  profileSecondaryButton: { minHeight: 46, padding: '10px 8px', border: '1px solid #d1d6db', borderRadius: 11, background: '#fff', color: '#4e5968', fontSize: 14, fontWeight: 700 },
  profilePrimaryButton: { minHeight: 46, padding: '10px 8px', border: 0, borderRadius: 11, background: 'var(--brand-primary)', color: '#fff', fontSize: 14, fontWeight: 700 },
  toast: { position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, color: '#fff', borderRadius: 10, padding: '10px 16px', boxShadow: '0 6px 20px rgba(0,0,0,0.18)', maxWidth: 'calc(100% - 32px)' },
  paymentProcessingOverlay: { position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box', background: 'rgba(15, 23, 42, 0.52)', backdropFilter: 'blur(2px)' },
  paymentProcessingCard: { width: '100%', maxWidth: 310, display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box', padding: '28px 22px 24px', borderRadius: 20, background: '#fff', boxShadow: '0 20px 55px rgba(15, 23, 42, 0.28)', textAlign: 'center' },
  paymentProcessingTitle: { marginTop: 16, color: '#191f28', fontSize: 18, lineHeight: 1.4 },
  paymentProcessingDescription: { margin: '8px 0 0', color: '#6b7684', fontSize: 13, lineHeight: 1.65 },
  overlay: { position: 'fixed', inset: 0, zIndex: 2500, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' },
  sheet: { width: '100%', maxWidth: 768, maxHeight: '82dvh', background: '#fff', borderRadius: '18px 18px 0 0', padding: 16, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' },
  sheetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 17, marginBottom: 12 },
  iconButton: { border: 0, background: 'transparent', padding: 6, display: 'flex' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e8eb', borderRadius: 10, padding: '0 12px', marginBottom: 12 },
  searchInput: { flex: 1, border: 0, outline: 0, padding: '12px 0', fontSize: 14, minWidth: 0 },
  resultArea: { overflowY: 'auto', minHeight: 180 },
  resultButton: { width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left', padding: '12px 4px', border: 0, borderBottom: '1px solid #f0f2f1', background: '#fff' },
  resultTitle: { fontSize: 15, fontWeight: 700, color: '#191f28' },
  resultMeta: { marginTop: 4, fontSize: 12, color: '#8b95a1' },
  empty: { textAlign: 'center', color: '#8b95a1', padding: '44px 8px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, paddingTop: 12 },
  pageButton: { border: '1px solid #e5e8eb', borderRadius: 8, background: '#fff', padding: '7px 12px' },
};
